import React, { useState, useEffect } from 'react';
import { FileText, Upload, Loader2, X, Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { toast } from 'sonner';
import { reservasApi } from '../../../lib/api';
import { VerazSection } from './VerazSection';
import { useAuth } from '../../../contexts/AuthContext';

/**
 * Modal de Cargar / Editar Reserva.
 * - mode: 'create' | 'edit'
 * - unidadesDisponibles: lista para multi-select (en create)
 * - reserva: objeto existente (en edit)
 * - onSaved: callback tras crear/editar exitoso
 */
export const ReservaDialog = ({
  open,
  onOpenChange,
  mode = 'create',
  edificioId,
  unidadesDisponibles = [],   // lista para crear (vacantes/admin)
  reserva = null,             // doc existente para editar
  onSaved,
}) => {
  const isEdit = mode === 'edit';
  const { user } = useAuth();
  const [form, setForm] = useState({
    unidades_ids: [],   // array de unidad_id seleccionadas
    razon_social: '',
    nombre_comercial: '',
    cuit: '',
    email: '',
    telefono: '',
    monto_total: '',
    moneda: 'ARS',
    fecha_reserva: new Date().toISOString().split('T')[0],
    fecha_inicio_estimada: '',
    fecha_firma_estimada: '',
    observaciones: '',
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && isEdit && reserva) {
      setForm({
        unidades_ids: (reserva.unidades_reserva || []).map(u => u.unidad_id),
        razon_social: reserva.razon_social || '',
        nombre_comercial: reserva.nombre_comercial || '',
        cuit: reserva.cuit || '',
        email: reserva.email || '',
        telefono: reserva.telefono || '',
        monto_total: reserva.monto_total != null ? String(reserva.monto_total) : '',
        moneda: reserva.moneda || 'ARS',
        fecha_reserva: reserva.fecha_reserva || '',
        fecha_inicio_estimada: reserva.fecha_inicio_estimada || '',
        fecha_firma_estimada: reserva.fecha_firma_estimada || '',
        observaciones: reserva.observaciones || '',
      });
      setFile(null);
    } else if (open && !isEdit) {
      setForm({
        unidades_ids: [],
        razon_social: '',
        nombre_comercial: '',
        cuit: '',
        email: '',
        telefono: '',
        monto_total: '',
        moneda: 'ARS',
        fecha_reserva: new Date().toISOString().split('T')[0],
        fecha_inicio_estimada: '',
        fecha_firma_estimada: '',
        observaciones: '',
      });
      setFile(null);
    }
  }, [open, isEdit, reserva]);

  const toggleUnidad = (unidadId) => {
    setForm(prev => ({
      ...prev,
      unidades_ids: prev.unidades_ids.includes(unidadId)
        ? prev.unidades_ids.filter(id => id !== unidadId)
        : [...prev.unidades_ids, unidadId],
    }));
  };

  // En modo edit incluimos la unidad ya reservada en el listado para que el usuario
  // pueda quitarla o agregar otras. Combinamos disponibles + las que ya estaban.
  const allUnidadesOptions = React.useMemo(() => {
    if (!isEdit || !reserva) return unidadesDisponibles;
    const yaReservadas = (reserva.unidades_reserva || []).map(ur => ({
      unidad_id: ur.unidad_id,
      nombre_unidad: ur.nombre_unidad || ur.unidad_id,
      tipo: '—',
      en_administracion: true,
    }));
    const ids = new Set(unidadesDisponibles.map(u => u.unidad_id));
    return [
      ...yaReservadas.filter(u => !ids.has(u.unidad_id)),
      ...unidadesDisponibles,
    ];
  }, [isEdit, reserva, unidadesDisponibles]);

  const handleSave = async () => {
    if (form.unidades_ids.length === 0) {
      toast.error('Seleccioná al menos una unidad');
      return;
    }
    setSaving(true);
    try {
      const unidades_reserva = form.unidades_ids.map(uid => {
        const u = allUnidadesOptions.find(x => x.unidad_id === uid);
        return {
          unidad_id: uid,
          nombre_unidad: u?.nombre_unidad || null,
          monto: 0,
        };
      });
      const payload = {
        edificio_id: edificioId,
        unidades_reserva,
        razon_social: form.razon_social || null,
        nombre_comercial: form.nombre_comercial || null,
        cuit: form.cuit || null,
        email: form.email || null,
        telefono: form.telefono || null,
        monto_total: form.monto_total ? parseFloat(String(form.monto_total).replace(/[^\d.-]/g, '')) : null,
        moneda: form.moneda || 'ARS',
        fecha_reserva: form.fecha_reserva || null,
        fecha_inicio_estimada: form.fecha_inicio_estimada || null,
        fecha_firma_estimada: form.fecha_firma_estimada || null,
        observaciones: form.observaciones || null,
      };

      let saved;
      if (isEdit) {
        const res = await reservasApi.update(reserva.reserva_id, payload);
        saved = res.data;
      } else {
        const res = await reservasApi.create(payload);
        saved = res.data;
      }

      // Si hay archivo nuevo, subirlo
      if (file) {
        try {
          await reservasApi.uploadArchivo(saved.reserva_id, file);
        } catch (err) {
          toast.warning('Reserva guardada pero no se pudo subir el archivo');
        }
      }

      toast.success(isEdit ? 'Reserva actualizada' : 'Reserva creada');
      onOpenChange(false);
      onSaved && onSaved(saved);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar la reserva');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="reserva-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-purple-600" />
            {isEdit ? 'Editar reserva' : 'Cargar reserva'}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Modificá los datos del precontrato. Al guardar, las unidades agregadas pasan a reservadas y las quitadas vuelven a vacantes.'
              : 'Datos del futuro inquilino y condiciones básicas. Una vez guardada, las unidades quedan reservadas hasta completar el contrato.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Unidades multi-select */}
          <div>
            <Label className="text-xs font-semibold">Unidades a reservar *</Label>
            <div className="mt-1 border border-slate-200 rounded-md p-2 max-h-40 overflow-y-auto bg-slate-50/50">
              {allUnidadesOptions.length === 0 ? (
                <p className="text-xs text-slate-500 italic">No hay unidades disponibles para reservar</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
                  {allUnidadesOptions.map(u => (
                    <label
                      key={u.unidad_id}
                      className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded cursor-pointer ${
                        form.unidades_ids.includes(u.unidad_id)
                          ? 'bg-purple-100 text-purple-800 ring-1 ring-purple-300'
                          : 'hover:bg-white'
                      }`}
                      data-testid={`reserva-unidad-opt-${u.unidad_id}`}
                    >
                      <input
                        type="checkbox"
                        checked={form.unidades_ids.includes(u.unidad_id)}
                        onChange={() => toggleUnidad(u.unidad_id)}
                        className="rounded"
                      />
                      <span className="truncate">{u.nombre_unidad}</span>
                      {u.tipo && u.tipo !== '—' && <span className="text-slate-400">({u.tipo})</span>}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {form.unidades_ids.length > 0
                ? `${form.unidades_ids.length} unidad${form.unidades_ids.length > 1 ? 'es' : ''} seleccionada${form.unidades_ids.length > 1 ? 's' : ''}`
                : 'Elegí una o más'}
            </p>
          </div>

          {/* Inquilino */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Razón social</Label>
              <Input
                value={form.razon_social}
                onChange={(e) => setForm({ ...form, razon_social: e.target.value })}
                placeholder="Inquilino S.A."
                data-testid="reserva-razon-social"
              />
            </div>
            <div>
              <Label className="text-xs">Nombre comercial</Label>
              <Input
                value={form.nombre_comercial}
                onChange={(e) => setForm({ ...form, nombre_comercial: e.target.value })}
                placeholder="Nombre fantasía"
                data-testid="reserva-nombre-comercial"
              />
            </div>
            <div>
              <Label className="text-xs">CUIT</Label>
              <Input
                value={form.cuit}
                onChange={(e) => setForm({ ...form, cuit: e.target.value })}
                placeholder="30-12345678-9"
                data-testid="reserva-cuit"
              />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="contacto@inquilino.com"
                data-testid="reserva-email"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Teléfono / WhatsApp</Label>
              <Input
                value={form.telefono}
                onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                placeholder="+54 261 ..."
                data-testid="reserva-telefono"
              />
            </div>
          </div>

          {/* Monto + Moneda */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Monto reservado / alquiler pactado</Label>
              <Input
                type="number"
                value={form.monto_total}
                onChange={(e) => setForm({ ...form, monto_total: e.target.value })}
                placeholder="0"
                data-testid="reserva-monto"
              />
            </div>
            <div>
              <Label className="text-xs">Moneda</Label>
              <Select value={form.moneda} onValueChange={(v) => setForm({ ...form, moneda: v })}>
                <SelectTrigger data-testid="reserva-moneda">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ARS">ARS</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Fechas */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Fecha de reserva</Label>
              <Input
                type="date"
                value={form.fecha_reserva}
                onChange={(e) => setForm({ ...form, fecha_reserva: e.target.value })}
                data-testid="reserva-fecha"
              />
            </div>
            <div>
              <Label className="text-xs">Inicio estimado</Label>
              <Input
                type="date"
                value={form.fecha_inicio_estimada}
                onChange={(e) => setForm({ ...form, fecha_inicio_estimada: e.target.value })}
                data-testid="reserva-inicio-est"
              />
            </div>
            <div>
              <Label className="text-xs">Firma estimada</Label>
              <Input
                type="date"
                value={form.fecha_firma_estimada}
                onChange={(e) => setForm({ ...form, fecha_firma_estimada: e.target.value })}
                data-testid="reserva-firma-est"
              />
            </div>
          </div>

          {/* Observaciones */}
          <div>
            <Label className="text-xs">Observaciones</Label>
            <Textarea
              value={form.observaciones}
              onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
              rows={2}
              placeholder="Aclaraciones, condiciones especiales..."
              data-testid="reserva-observaciones"
            />
          </div>

          {/* Archivo */}
          <div>
            <Label className="text-xs">
              Documento de reserva {isEdit && reserva?.archivo && <span className="text-slate-400">(reemplazar)</span>}
            </Label>
            {isEdit && reserva?.archivo && (
              <p className="text-xs text-slate-500 mb-1">
                Actual: <span className="font-mono">{reserva.archivo.nombre}</span>
              </p>
            )}
            <input
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm border border-slate-200 rounded p-1.5"
              data-testid="reserva-file-input"
            />
          </div>

          {/* Veraz — solo disponible en modo edit (necesita reserva_id) */}
          {isEdit && reserva?.reserva_id && (
            <div className="pt-3 border-t border-slate-200">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2 block">
                Veraz (PDFs)
              </Label>
              <VerazSection
                reservaId={reserva.reserva_id}
                userRol={user?.rol}
                defaultInquilinoNombre={form.razon_social || form.nombre_comercial || ''}
              />
            </div>
          )}
          {!isEdit && (
            <p className="text-[11px] text-slate-400 italic">
              Después de crear la reserva podrás adjuntar archivos Veraz desde el modal de edición.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || form.unidades_ids.length === 0}
            data-testid="reserva-save-btn"
            className="bg-purple-600 hover:bg-purple-700"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Guardando...
              </>
            ) : isEdit ? 'Guardar cambios' : 'Crear reserva'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReservaDialog;
